import { arrayMove } from '@dnd-kit/sortable';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Stack,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DndArticleListSection from './info-order/DndArticleListSection';
import CloseIcon from '@mui/icons-material/Close';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import {
	useGetFaqListQuery,
	useSortFaqListMutation,
} from '@/api/information/faqApiSlice';
import { SortFaqListDTO } from '@/api/DTO/information/faq.interface';

interface Props {}

export default function SetOrderDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [sortFaqList] = useSortFaqListMutation();

	const [faqItems, setFaqItems] = useState<any>([]);

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFaqListQuery(
			{
				pagination: { page: 1, size: 1000000 },
				searchString: '',
				sortings: [],
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	useEffect(() => {
		setFaqItems(propertySummary?.data?.items ?? []);
	}, [isGetPropertySummarySuccess, propertySummary]);

	const handleRepositionItem = (activeIndex: number, overIndex: number) => {
		setFaqItems((prev: unknown[]) => arrayMove(prev, activeIndex, overIndex));
	};

	const onSubmit = async () => {
		const data: SortFaqListDTO[] = [];
		faqItems.forEach((item: { id: string }, index: number) => {
			data.push({
				id: item.id,
				sort: index,
			});
		});
		const response = await sortFaqList({ sortList: data });
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.sortSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<FormatLineSpacingIcon />}
				onClick={() => setOpen(true)}
			>
				{t('faq.rearrangement')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('faq.rearrangement')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<Stack>
								{faqItems && (
									<DndArticleListSection
										faqItems={faqItems}
										handleRepositionItem={handleRepositionItem}
									/>
								)}
							</Stack>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
